import React, { useState } from 'react';
import AtlasV2DoubleDropdownMetricColumnChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DoubleDropdownMetricColumnChart';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { HIDE_GROUP_BY_LEVEL } from 'src/components/Layout/AtlasV2PageLayout';
import { AdvancedSearchParsedDatum } from 'src/serverProxy/useAdvancedSearchData';
import useAtlasV2AdvancedSearchData from 'src/serverProxy/useAtlasV2AdvancedSearchData';
import { safeDivide } from 'src/utils/app';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';

const calculateRatio = (promotionValue, retailValue) => safeDivide(promotionValue, retailValue);

// function to merge sales data and calculate ratios
const mergeSalesData = (promotionSales, retailSales) => {
  const retailSalesMap = new Map(retailSales.map((item) => [item.fieldId, item]));

  return promotionSales
    .map((promotionItem) => {
      const retailItem = retailSalesMap.get(promotionItem.fieldId);
      // If no matching retail item  is found, return null
      if (!retailItem) {
        return null;
      }
      return {
        ...promotionItem,
        fieldId: promotionItem.fieldId,
        categoryName: promotionItem.metadata?.name,
        values: {
          retailSales_sum_value: calculateRatio(
            promotionItem.values.retailSales_sum_value ?? 0,
            retailItem.values.retailSales_sum_value
          )
        },
        comparisonValues: {
          retailSales_sum_value: calculateRatio(
            promotionItem.comparisonValues.retailSales_sum_value ?? 0,
            retailItem.comparisonValues.retailSales_sum_value
          )
        }
      };
    })
    .filter(Boolean);
};

const promotionCategoryTopEntityGroupByOptions = [
  {
    id: 'categoryId',
    label: 'Category',
    indexName: 'promotions'
  },
  {
    id: 'subCategoryId',
    label: 'Subcategory',
    indexName: 'promotions'
  },
  {
    id: 'brandId',
    label: 'Brand',
    indexName: 'promotions'
  },
  { id: 'parentBrandId', label: 'Manufacturer', indexName: 'promotions' }
];

const AtlasV2PromotionsByCategoryTopEntityChart = () => {
  const entityType = useMainEntityType();
  const [selectedGroupBy, setSelectedGroupBy] = useState('categoryId');

  const [fieldDisplayName, setFieldDisplayName] = useState('?');
  const { data: retailSalesInSalesIndexData, isLoading: isRetailSalesInSalesIndexLoading } =
    useAtlasV2AdvancedSearchData({
      indexName: 'sales',
      fields: [
        {
          name: 'retailSales'
        }
      ],
      groupByField: selectedGroupBy,
      requestId: 'sales-retailSales-by-group',
      primaryGenericAdvancedSearchArgs: {
        shouldPerformFetch: fieldDisplayName === 'Share of Total Sales'
      }
    });

  return entityType === 'product' ? null : (
    <AtlasV2DoubleDropdownMetricColumnChart
      isExtraLoading={isRetailSalesInSalesIndexLoading}
      useAdvancedSearchDataArgs={{
        requestId: `Promotions topEntity by Group`,
        requestBuilder: (builder, _, fields) => {
          if (['dataPointCount', 'retailPriceChangePercent'].includes(fields?.[0]?.name)) {
            builder
              .modifyAggregation((agg) =>
                agg
                  .addAggregationField('Promotion Sales', 'retailSales', 'sum', 'true')
                  .setSortByAggregationField('Promotion Sales', 'retailSales', 'sum', 'true')
              )
              .addConditionRangeFilter('retailSales', 0.1)
              .setSearchBy('parent');
          }

          builder.setPageSize(250);

          return builder;
        }
      }}
      preProcessData={(data: AdvancedSearchParsedDatum[], field, groupByField) => {
        // We use displayName because the index is retailSales that will override with Share of Total Sales
        switch (field.displayName) {
          case 'Promotion Count':
            return data.map((datum) => ({
              ...datum,
              values: {
                ...datum.values,
                dataPointCount_sum_value: datum?.values?.dataPointCount_sum_count ?? 0
              },
              comparisonValues: {
                ...datum.comparisonValues,
                dataPointCount_sum_value: datum?.comparisonValues?.dataPointCount_sum_count ?? 0
              }
            }));

          case 'Share of Total Sales': {
            if (groupByField !== selectedGroupBy || fieldDisplayName !== field.displayName) {
              setSelectedGroupBy(groupByField);
              setFieldDisplayName(field.displayName);
            }

            return mergeSalesData(data, retailSalesInSalesIndexData);
          }

          default:
            return data;
        }
      }}
      postProcessData={(data) => {
        if (data) {
          return data.map((row) => {
            const [group, value] = row;
            return [group, Math.abs(value)];
          });
        } else {
          return data;
        }
      }}
      beaconChartWithLegendPropsBuilder={(props) => ({
        ...props,
        loadingComponent: <TopEntityChartV2Loading />
      })}
      fields={[
        { fieldName: 'dataPointCount', label: 'Promotion Count' },
        { fieldName: 'retailSales', label: 'Promotion Sales' },
        { fieldName: 'promotionRetailSales', label: 'Share of Total Sales' },
        { fieldName: 'retailPriceChangePercent', label: 'Average Discount' }
      ]}
      groupByFields={promotionCategoryTopEntityGroupByOptions.filter(
        (option) =>
          !option.id.toLowerCase().startsWith(entityType) && HIDE_GROUP_BY_LEVEL?.[entityType]?.[option.id] !== true
      )}
    />
  );
};

export default AtlasV2PromotionsByCategoryTopEntityChart;
